import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import "./CampusDiary.css";

const WINDOW = 3; // how many cards render either side of the active one
const STEP = 1500; // how long each photo holds — short enough to read as a
                   // continuous turn rather than a slideshow that clicks over

const CampusDiary = ({ categories }) => {
  const [cat, setCat] = useState(0);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const stageRef = useRef(null);
  const wheelAcc = useRef(0);
  const drag = useRef({ x: 0, moved: false, down: false });

  const images = categories[cat].images;
  const count = images.length;

  // centre the coverflow whenever the category changes
  useEffect(() => {
    setActive(Math.floor(categories[cat].images.length / 2));
  }, [cat, categories]);

  /* The diary turns itself over. It wraps rather than stopping at the ends,
     so the strip runs continuously; any interaction — hover, focus, a drag,
     an arrow — pauses it while the visitor is actually looking. */
  useEffect(() => {
    if (paused || count < 2) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setActive((a) => (a + 1) % count), STEP);
    return () => clearInterval(t);
  }, [paused, count, cat]);

  // wraps both ways, so neither arrow is ever a dead end
  const go = (dir) => setActive((a) => (a + dir + count) % count);

  const pickCategory = (i) => {
    if (i !== cat) setCat(i);
  };

  const onWheel = (e) => {
    // only hijack clearly-horizontal intent so vertical page scroll still works
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    e.preventDefault();
    wheelAcc.current += e.deltaX;
    if (Math.abs(wheelAcc.current) > 45) {
      go(wheelAcc.current > 0 ? 1 : -1);
      wheelAcc.current = 0;
    }
  };

  const onPointerDown = (e) => {
    drag.current = { x: e.clientX, moved: false, down: true };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.down || d.moved) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 55) {
      go(dx > 0 ? -1 : 1);
      d.moved = true;
    }
  };
  const endDrag = () => {
    drag.current.down = false;
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      go(1);
    }
  };

  const activeCaption = images[active]?.caption;

  return (
    <div className="diary">
      <div className="diary-pills" role="tablist" aria-label="Photo categories">
        {categories.map((c, i) => (
          <button
            key={c.label}
            role="tab"
            aria-selected={i === cat}
            className={`diary-pill ${i === cat ? "is-active" : ""}`}
            onClick={() => pickCategory(i)}
          >
            {c.label}
            <span className="diary-pill-n">{c.images.length}</span>
          </button>
        ))}
      </div>

      <div
        ref={stageRef}
        className="diary-stage"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={(e) => {
          endDrag(e);
          setPaused(false);
        }}
        onMouseEnter={() => setPaused(true)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="group"
        aria-label={`${categories[cat].label}: photo ${active + 1} of ${images.length}`}
      >
        {images.map((img, i) => {
          const offset = i - active;
          const abs = Math.abs(offset);
          if (abs > WINDOW) return null;
          const dir = Math.sign(offset);
          const style = {
            transform: `translateX(${offset * 58}%) translateZ(${-abs * 130}px) rotateY(${dir * -22}deg) scale(${offset === 0 ? 1 : 0.92})`,
            zIndex: 50 - abs,
            opacity: abs > WINDOW - 0.5 ? 0 : 1,
          };
          return (
            <figure
              key={i}
              className={`diary-card ${offset === 0 ? "is-active" : ""}`}
              style={style}
              onClick={() => (offset === 0 ? null : setActive(i))}
              aria-hidden={offset !== 0}
            >
              <img src={img.src} alt={img.caption || `${categories[cat].label} ${i + 1}`} draggable="false" />
              {offset !== 0 && <span className="diary-card-veil" />}
            </figure>
          );
        })}
      </div>

      <div
        className="diary-foot"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <button className="diary-nav" onClick={() => go(-1)} aria-label="Previous photo">
          <FontAwesomeIcon icon={faAngleLeft} />
        </button>

        <p className="diary-caption">
          {activeCaption || (
            <span className="diary-counter">
              {String(active + 1).padStart(2, "0")}
              <i>/</i>
              {String(images.length).padStart(2, "0")}
            </span>
          )}
        </p>

        <button className="diary-nav" onClick={() => go(1)} aria-label="Next photo">
          <FontAwesomeIcon icon={faAngleRight} />
        </button>
      </div>
    </div>
  );
};

export default CampusDiary;
